import OlVectorLayer from 'ol/layer/Vector';
import OlVectorSource from 'ol/source/Vector';
import OlClusterSource from 'ol/source/Cluster';
import { WFS as OlWFS, GeoJSON as OlGeoJSON } from 'ol/format';
import { intersects } from 'ol/extent';
import { NEDGLOBE_VIEWER, APPLICATION_JSON, FEEDBACKS_GEOMETRY_TYPES, NG_LAYER_TYPES } from '../../../../constants';
import { opengisNamespace, ogcCreateFilterValues, ogcPropertyOperatorFromSoftDelete, setIntersectsFilter } from '../filter';
import { getStyle, getLayerClusterStyle } from '../styles';
import { layerFeaturesCanBePaged } from '../../../../services/map/feature/selection';

/**
* [createWfsLayerObject description]
* @param  {[type]} layerData    [description]
* @param  {[type]} request [description]
* @param  {[type]} wms     [description]
* @param  {[type]} empty   [description]
* @return {[type]}         [description]
*/
export function createWfsLayerObject(store, layerData, hideFill = false) {
    // Determine request URL (direct or via proxy)
    // Set URL for WFS requests
    // Don't add filter in URL, it's created by giving options to wfsFactory (GetFeatureRequest)
    const source = layerData.useCluster ? getClusterSource(store, layerData) : getVectorSource(store, layerData);
    // set hideFill in computedStyles to make it available in GetStyle function
    layerData.computedStyles[0].hideFill = hideFill;

    const styleFunction = (feature, resolution) => {
        return layerData.useCluster ?
            getLayerClusterStyle(layerData, feature, resolution, store.getters.publicEndPoint) :
            getStyle(
                layerData.computedStyles,
                feature,
                resolution,
                NEDGLOBE_VIEWER.styleDisplayTypes.default,
                layerData.feedbackFields
            );
    };

    if (layerData.type === NG_LAYER_TYPES.CUSTOM) {
        return createWfsCustomLayerObject(store, layerData, styleFunction);
    } else {
        // Give the layer a className to declutter only within the layer itself.
        return new OlVectorLayer({
            renderMode: 'image',
            title: layerData.title,
            id: layerData.id,
            className: layerData.id,
            combinedId: layerData.getCombinedOlId(),
            type: 'WFS',
            visible: true,
            // Grafische laag WFS lagen
            layerWMS: layerData.graphicalLayerName,
            // layer source
            source,
            // layer style
            style: styleFunction,
            // declutter is used to avoid overlapping labels/images
            declutter: true
        });
    }
}

function createWfsCustomLayerObject(store, layerData, styleFunction) {
    return new OlVectorLayer({
        source: new OlVectorSource({
            format: new OlGeoJSON(),
            loader: function (extent) {
                // When working with WFS services in OpenLayers that provide paginated results,
                // load the features in multiple requests and combine them into a single vector source
                const separator = layerData.serviceUri.includes('?') ? '&' : '?';
                const baseUrl = `${layerData.serviceUri}${separator}service=WFS&version=${layerData.wfsVersion}&request=GetFeature` +
                    `&typeName=${layerData.layerName}&outputFormat=application/json&bbox=${extent.join(',')}`;

                // Only page for >v2 WFS
                const usePaging = layerFeaturesCanBePaged(layerData);

                const maxFeatures = store.state.userData.maxFeatures;
                let startIndex = 0;

                const loadPage = async () => {
                    try {
                        let url = baseUrl;
                        if (usePaging) {
                            url += `&startIndex=${startIndex}&maxFeatures=${maxFeatures}`;
                        }

                        // Await the fetch call to complete
                        const response = await fetch(url);

                        // Await the conversion to JSON
                        const data = await response.json();

                        const features = this.getFormat().readFeatures(data);
                        this.addFeatures(features);

                        if (usePaging && features.length === maxFeatures) {
                            // If the page is full, load the next page
                            startIndex += maxFeatures;
                            loadPage();
                        }
                    } catch (error) {
                        console.error('Error loading WFS features:', error);
                    }
                };
                // Start loading the first page
                loadPage();

            },
            projection: layerData.projection,
            strategy: CustomOlLoadingStrategyClearExtents // Load features dynamically based on extent
        }),
        style: styleFunction,
    });
}

/**
* [getClusterSource description]
* @param  {object} layerData the themelayer
* @return {OlClusterSource} OlVectorSource for WFS layer or Cluster display
*/
function getClusterSource(store, layerData) {
    return new OlClusterSource({
        distance: 100, // Determines the density of the cluster grid in pixels, value is updated on map zoom change
        source: getVectorSource(store, layerData),
        geometryFunction: (feature) => {
            // Ignore lines/polygons when clustering
            const geometry = feature.getGeometry();
            return geometry.getType() === FEEDBACKS_GEOMETRY_TYPES.POINT ? geometry : null;
        }
    });
}

/**
* [getVectorSource description]
* @param  {object} layerData the themelayer
* @return {OlVectorSource} OlVectorSource for WFS layer or Cluster display
*/
export function getVectorSource(store, layerData) {
    // Determine if we need to hide the polygon fill of a WFS layer
    // If so we will not create the wfswms layer but only the WFS layer
    // we will create this WFS layer with an extra parameter to indicate that
    // no fill is required. We will only do so when alwaysCreateWfsLayer is false.
    // When alwaysCreateWfsLayer is true this means we are creating an extra WFS layer for text information
    const hidePolygonFill = store.getters.getLayerhidePolygonFillById(layerData.customId);

    let cancellationToken;
    return new OlVectorSource({
        loader: async function (extent) {
            if (cancellationToken) {
                store.commit('ACTIVATE_SELECTION_CANCELLATION_TOKEN', cancellationToken);
            }
            cancellationToken = store.getters.getSelectionCancelToken();
            const layerId = store.getters.isMobile ? layerData.getCombinedOlId() : layerData.id;
            try {
                const features = await store.dispatch('getFeaturesFromBBox', { layerId, bbox: extent, forceLoading: true, cancellationToken });
                cancellationToken = null;
                layerData.computedStyles[0].hideFill = hidePolygonFill;
                const styleFunction = (feature, resolution) => {
                    return getStyle(
                        layerData.computedStyles,
                        feature,
                        resolution,
                        NEDGLOBE_VIEWER.styleDisplayTypes.default,
                        layerData.feedbackFields
                    );
                };
                // Remove existing features that are outside the new extent
                this.getFeatures().forEach(feature => {
                    const featureExtent = feature.getGeometry().getExtent();
                    // Remove features that do not intersect with the new extent
                    if (!intersects(extent, featureExtent)) {
                        this.removeFeature(feature);
                    }
                });

                features.forEach((feature) => {
                    feature.setStyle(styleFunction);
                    // sets layerId for clusters
                    feature.setProperties({ layerId });
                    this.addFeature(feature);
                });
            } catch (error) {
                // Code removed to mark layer error.
                // Many errors are generated when zooming in and out quickly. No need to mark an error on the layer
            }
        },
        projection: layerData.projection,
        strategy: CustomOlLoadingStrategyClearExtents //
    });
}

/**
* Custom OL loading strategy, clear extents in all cases
* This enforces reloading all features
* Previously, after zooming out, not all features were shown ( > 1000 features ), after zooming in, unknown features were assumed to be already loaded
* @param {Extent} extent Extent.
* @param {number} resolution Resolution.
* @return {Array<Extent>} Extents.
*/
const CustomOlLoadingStrategyClearExtents = function clearextents(extent, resolution) {
    // this seems to be VectorSource
    // If current extent is equal to Rtree extent, skip clearing the extents (do not retrieve features when map has not moved)
    const skipClearExtents = this.loadedExtentsRtree_.rbush_.data.minX === extent[0] &&
        this.loadedExtentsRtree_.rbush_.data.minY === extent[1] &&
        this.loadedExtentsRtree_.rbush_.data.maxX === extent[2] &&
        this.loadedExtentsRtree_.rbush_.data.maxY === extent[3];

    // Clear extents so new features are always rendered
    if (!skipClearExtents) {
        this.loadedExtentsRtree_.clear();
    }

    return [extent];
};

export function GetVersionAndOutput(layerData) {
    let version = '2.0.0';

    // Het wfsOutputformat from layerData
    let output = APPLICATION_JSON;
    if (layerData.source.sourceWfsVersion < 2.0 && layerData.source.sourceWfsVersion !== 0) {
        version = '1.1.0';

        // Cyclomedia WFS has older WFS version but only works with output = "application/json"
        if (layerData.layerName !== 'atlas:Recording') {
            output = 'json';
        }
    }

    return { version: version, output: output };
}

export function getFeatures(getFeatureOptions, count = null, startIndex = 0) {
    const options = { ...getFeatureOptions };
    // If interSectsFilter is true, then replace bbox filter with intersection filter for exact feature retrieval
    if (options.intersectsFilter) {
        setIntersectsFilter(options);
    }
    // set version number
    if (!options.version) {
        if (options.sourceVersion < 2) {
            options.version = '1.1.0';
        } else {
            options.version = '2.0.0';
            options.count = count || options.maxFeatures;
            options.maxFeatures = undefined;
            options.startIndex = startIndex;
        }
    }

    // Fix for execution path where sourceVersion empty but we do have a version.
    if (!options.sourceVersion) {
        if (options.version === '1.1.0') {
            options.sourceVersion = 1.1;
        } else {
            options.sourceVersion = 2;
        }
    }

    // Get base feature request from openlayers
    const xml = (new OlWFS({ version: options.version })).writeGetFeature(options);
    // get the correct namespace for the version
    const createdOpengisNamespace = opengisNamespace(options.sourceVersion);
    // Create filter;
    const ogcFilter = document.createElementNS(createdOpengisNamespace, 'Filter');
    const ogcAnd = document.createElementNS(createdOpengisNamespace, 'And');
    let ogcOr = null;
    if (options.filters?.length > 0) {
        const containsArrays = options.filters.every((filter) => {
            return (filter instanceof Array);
        });
        if (containsArrays) {
            ogcOr = document.createElementNS(createdOpengisNamespace, 'Or');
            ogcCreateFilterValuesOR(createdOpengisNamespace, options, ogcOr);
        } else {
            // Loop over filters (multiple are possible)
            ogcCreateFilterValues(options.sourceVersion, options.filters, ogcAnd, options.isFeedback);
        }
    }
    // Feedback: possible with multiple Where clause(s) and searchAttribute
    if (options.isFeedback === true) {
        // Add feedback search-filters
        appendFilterFeedback(xml, ogcFilter, options, ogcAnd);

    } else if (options.filters?.length > 0) {
        // Themelayer: with AttributeFilter(s) and searchAttribute
        appendFilterThemeLayer(xml, ogcFilter, options, ogcAnd, ogcOr);
    }

    // Append sort properties
    appendSortBy(xml, createdOpengisNamespace, options);

    // Return serialized xml
    return new XMLSerializer().serializeToString(xml);
}

function ogcCreateFilterValuesOR(createdOpengisNamespace, options, ogcOr) {
    options.filters.forEach((filter) => {
        if (filter.length === 1) {
            ogcCreateFilterValues(options.sourceVersion, filter, ogcOr);
        } else {
            const ogcAnd = document.createElementNS(createdOpengisNamespace, 'And');
            ogcCreateFilterValues(options.sourceVersion, filter, ogcAnd);
            ogcOr.appendChild(ogcAnd);
        }
    });
}

function appendFilterFeedback(xml, ogcFilter, options, ogcAnd) {
    // Add feedback soft delete filter
    const propertyoperatorSoftDelete = ogcPropertyOperatorFromSoftDelete(options.sourceVersion);
    ogcAnd.appendChild(propertyoperatorSoftDelete);
    // BBOX
    ogcAnd.appendChild(xml.firstChild.lastChild.firstChild);
    // Remove old filter for rebuilding
    if (xml.firstChild.lastChild.nodeName === 'Filter') {
        xml.firstChild.removeChild(xml.firstChild.lastChild);
    }
    // Add ogcAnd
    ogcFilter.appendChild(ogcAnd);
    // Add new
    xml.firstChild.appendChild(ogcFilter);
}

function appendFilterThemeLayer(xml, ogcFilter, options, ogcAnd, ogcOr) {
    // BBOX
    const bbox = xml.firstChild?.lastChild?.firstChild;
    if (bbox) {
        ogcAnd.appendChild(xml.firstChild.lastChild.firstChild);
    }
    if (ogcOr) {
        // Add 'ogcOr' to filter
        ogcFilter.appendChild(ogcOr);
    } else {
        if (!bbox && options.filters.length === 1) {
            const ogcPropertyWithoutEnd = ogcAnd.firstChild;
            // Add property to filter
            ogcFilter.appendChild(ogcPropertyWithoutEnd);
        } else {
            // Add 'ogcAnd' to filter
            ogcFilter.appendChild(ogcAnd);
        }
    }
    if (xml.firstChild?.lastChild?.nodeName === 'Filter') {
        xml.firstChild.removeChild(xml.firstChild.lastChild);
    }
    // Append new filter
    xml.firstChild.appendChild(ogcFilter);
}

function appendSortBy(xml, createdOpengisNamespace, options) {
    if (options.sortProperties?.length > 0) {
        const ogcSortBy = document.createElementNS(createdOpengisNamespace, 'SortBy');
        options.sortProperties.forEach(prop => {
            const ogcSortProperty = document.createElementNS(createdOpengisNamespace, 'SortProperty');
            const ogcPropertyName = document.createElementNS(createdOpengisNamespace, 'ValueReference');
            const ogcSortOrder = document.createElementNS(createdOpengisNamespace, 'SortOrder');

            ogcPropertyName.appendChild(document.createTextNode(prop.sortProperty));
            ogcSortOrder.appendChild(document.createTextNode(prop.sortOrder));

            ogcSortProperty.appendChild(ogcPropertyName);
            ogcSortProperty.appendChild(ogcSortOrder);

            ogcSortBy.appendChild(ogcSortProperty);
        });
        xml.firstChild.appendChild(ogcSortBy);
    }
}
